import { ReactElement } from "react";

import PdfViewer from "src/components/pdf-viewer";

interface PreviewPanelProps {
  previewUrl: string | null;
  generating: boolean;
  disabled: boolean;
  onGenerate: () => void;
}

/**
 * On-demand PDF preview. The endpoint generates the document without storing
 * or distributing it, so this can be run as often as needed.
 */
const PreviewPanel: React.FC<PreviewPanelProps> = ({
  previewUrl,
  generating,
  disabled,
  onGenerate,
}): ReactElement => (
  <div className="panel">
    <header>
      <h4>Document preview</h4>
      {previewUrl && (
        <button
          type="button"
          className="btn ghost"
          disabled={disabled}
          onClick={onGenerate}
        >
          Regenerate preview
        </button>
      )}
    </header>
    <div className="body">
      {previewUrl ? (
        /* Keep the document on screen while a new one is generated — blanking
           it on every date change makes the page flicker. */
        <div className={`preview-shell${generating ? " is-busy" : ""}`}>
          <div className="preview-frame">
            <PdfViewer fileUrl={previewUrl} />
          </div>
          {generating && (
            <div className="preview-overlay" role="status">
              <span>
                <span className="date-field__spinner" aria-hidden="true" />
                Updating document…
              </span>
            </div>
          )}
        </div>
      ) : generating ? (
        <div className="preview-empty">
          <p>Generating the document…</p>
        </div>
      ) : (
        <div className="preview-empty">
          <p>
            The document could not be generated. It is produced on demand and
            is neither stored nor sent to anyone.
          </p>
          <button
            type="button"
            className="btn primary"
            disabled={disabled}
            onClick={onGenerate}
          >
            Try again
          </button>
        </div>
      )}
    </div>
  </div>
);

export default PreviewPanel;
